import { useState, FC, useEffect } from "react";

import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Controller, useFormContext } from "react-hook-form";

import { setHours, setMinutes } from "date-fns";
import { DateTime } from "luxon";

import { ServiceOrderType } from "@/_constants/serviceOrder";
import {
  safeToDateTime,
  safeToJSDate,
  parseUserProposedDate,
} from "@/_utils/safeDateTime";
import Switch from "@/common/components/form/Switch.tsx";

type Props = {
  name: string;
};

const ServiceOrderingAppointmentForm: FC<Props> = ({ name }) => {
  const { control, getValues, watch, setValue } = useFormContext();
  const type = getValues("inventory.type");

  const label = () => {
    switch (type) {
      case ServiceOrderType.INCOMING_INVENTORY:
        return "Date d'entrée des locataires";
      case ServiceOrderType.OUTGOING_INVENTORY:
      case ServiceOrderType.OUTGOING_INVENTORY_WITH_COSTING:
        return "Date de sortie des locataires";
      case ServiceOrderType.OUTGOING_INCOMING_INVENTORY:
        return "Date de sortie et d'entrée des locataires";
      default:
        return "Date d'intervention souhaitée";
    }
  };

  const isOutgoingType =
    type === ServiceOrderType.OUTGOING_INVENTORY ||
    type === ServiceOrderType.OUTGOING_INCOMING_INVENTORY ||
    type === ServiceOrderType.OUTGOING_INVENTORY_WITH_COSTING;
  const isIncomingType =
    type === ServiceOrderType.INCOMING_INVENTORY ||
    type === ServiceOrderType.OUTGOING_INCOMING_INVENTORY;

  const minDate = watch("tenantLeaseStartDate")
    ? new Date(watch("tenantLeaseStartDate"))
    : new Date();
  const isManagedByMainGroup = watch("isManagedByMainGroup");
  const currentAppointmentDate = watch(`${name}.appointment.startDate`);
  const tenantLeaseEndDate = watch("tenantLeaseEndDate");

  const [addAppointment, setAddAppointment] = useState<boolean>(() => {
    return !isManagedByMainGroup || Boolean(currentAppointmentDate);
  });

  useEffect(() => {
    if (isManagedByMainGroup && currentAppointmentDate && !addAppointment) {
      setAddAppointment(true);
    }
    else if (!isManagedByMainGroup) {
      setAddAppointment(true);
    }
  }, [isManagedByMainGroup, currentAppointmentDate, addAppointment]);

  useEffect(() => {
    if (addAppointment && isManagedByMainGroup) {
      const externalFormData = getValues("externalFormData");
      if (externalFormData?.userProposedAppointmentDate) {
        const parsedDate = parseUserProposedDate(
          externalFormData.userProposedAppointmentDate
        );
        if (parsedDate) {
          setValue(`${name}.appointment.startDate`, parsedDate);
        }
      }
    }
  }, [addAppointment, setValue, name, getValues, isManagedByMainGroup]);

  return (
    <>
      <div className="serviceOrdering-subtitle">Dates</div>
      <div className="serviceOrdering_dates-container">
        <div className="serviceOrdering_tenant">
          {(isOutgoingType || isIncomingType) && (
            <div className="d-flex flex-wrap align-items-center gap-4">
              {/* Tenant Lease End Date (Outgoing) */}
              {isOutgoingType && (
                <div className="form-input d-flex flex-column">
                  <label className="form-input_label">
                    Date de fin de bail occupant(s) sortant
                    <span className="form-input_label-required">&nbsp;*</span>
                  </label>
                  <Controller
                    control={control}
                    name="tenantLeaseEndDate"
                    render={({ field, fieldState }) => (
                      <>
                        <DatePicker
                          className={`w-100 ${
                            fieldState.error && "form-input_field-error"
                          } form-input_field date-select`}
                          dateFormat="dd/MM/yyyy"
                          locale="fr"
                          onChange={(date) =>
                            field.onChange(safeToDateTime(date))
                          }
                          selected={safeToJSDate(field.value)}
                        />
                        {fieldState?.error && (
                          <span className="form-input_error-message">
                            {fieldState.error?.message}
                          </span>
                        )}
                      </>
                    )}
                    rules={{
                      required: "Veuillez renseigner une date de fin de bail",
                    }}
                  />
                </div>
              )}

              {/* Tenant Lease Start Date (Incoming) */}
              {isIncomingType && (
                <div className="form-input d-flex flex-column">
                  <label className="form-input_label">
                    Date de signature du bail occupant(s) entrant
                    <span className="form-input_label-required">&nbsp;*</span>
                  </label>
                  <Controller
                    control={control}
                    name="tenantLeaseStartDate"
                    render={({ field, fieldState }) => (
                      <>
                        <DatePicker
                          className={`w-100 ${
                            fieldState.error && "form-input_field-error"
                          } form-input_field date-select`}
                          dateFormat="dd/MM/yyyy"
                          locale="fr"
                          // minDate={new Date()}
                          maxDate={
                            tenantLeaseEndDate &&
                            safeToJSDate(tenantLeaseEndDate)
                          }
                          onChange={(date) =>
                            field.onChange(safeToDateTime(date))
                          }
                          selected={safeToJSDate(field.value)}
                        />
                        {fieldState?.error && (
                          <span className="form-input_error-message">
                            {fieldState.error?.message}
                          </span>
                        )}
                      </>
                    )}
                    rules={{
                      required:
                        "Veuillez renseigner une date de signature de bail",
                    }}
                  />
                </div>
              )}

              {/* Insurance Switch (Incoming Only) */}
              {isIncomingType && (
                <div className="form-input d-flex flex-column">
                  <label className="form-input_label">
                    Attestation d’assurance remise en agence ?
                  </label>
                  <Switch
                    label=""
                    control={control}
                    name="inventory.isInsuranceIssuedByAgency"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="serviceOrdering_appointment-element">
          <div className="form-input justify-content-between gap-2">
            <div className="d-flex align-items-center gap-4">
              {/* Radio buttons */}
              {isManagedByMainGroup && (
                <Form.Group className="d-flex flex-column mt-3">
                  <Form.Check
                    className="form-input_label"
                    checked={!addAppointment}
                    onChange={() => {
                      setAddAppointment(false);
                      setValue(`${name}.appointment.startDate`, undefined);
                    }}
                    type="radio"
                    name={`${name}.appointmentChoice`}
                    value="dwell"
                    label="Je laisse Dwell fixer l’heure et la date du rendez-vous"
                  />
                  <Form.Check
                    className="form-input_label"
                    checked={addAppointment}
                    onChange={() => setAddAppointment(true)}
                    type="radio"
                    name={`${name}.appointmentChoice`}
                    value="propose"
                    label="Je propose une heure et une date de rendez-vous"
                  />
                </Form.Group>
              )}

              {/* Date Picker */}
              {(addAppointment || !isManagedByMainGroup) && (
                <div className="d-flex flex-column ">
                  <span className="form-input_label mb-1">
                    {label()}
                    <span className="form-input_label-required">&nbsp;*</span>
                  </span>
                  <Controller
                    control={control}
                    name={`${name}.appointment.startDate`}
                    render={({ field, fieldState }) => {
                      if (!field.value) {
                        const defaultDate = DateTime.now().set({
                          hour: 8,
                          minute: 0,
                        });
                        field.onChange(safeToDateTime(minDate, defaultDate));
                      }
                      return (
                        <div>
                          <DatePicker
                            className={`w-100 ${
                              fieldState.error && "form-input_field-error"
                            } form-input_field date-select`}
                            dateFormat="dd/MM/yyyy HH:mm"
                            locale="fr"
                            maxDate={
                              tenantLeaseEndDate &&
                              safeToJSDate(tenantLeaseEndDate)
                            }
                            showTimeSelect
                            timeCaption="Heure :"
                            timeFormat="HH:mm"
                            timeIntervals={30}
                            minTime={setHours(setMinutes(new Date(), 0), 8)}
                            maxTime={setHours(setMinutes(new Date(), 0), 19)}
                            onChange={(date) =>
                              date && field.onChange(safeToDateTime(date))
                            }
                            selected={safeToJSDate(field.value)}
                          />
                          {fieldState?.error && (
                            <span className="form-input_error-message">
                              {fieldState.error?.message}
                            </span>
                          )}
                        </div>
                      );
                    }}
                    rules={{ required: "Veuillez renseigner une date" }}
                  />
                </div>
              )}
            </div>{" "}
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceOrderingAppointmentForm;
